/**
 * @file        src/components/Icon.jsx
 * @description Stroke-based inline SVG icon set, ported from the approved mock.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-08-04 Claude — Added `mail` (envelope) icon for the "Share by email"
 *                          row action (CR D2).
 *   - 2026-08-04 Claude — Added `more` (kebab/vertical-dots) icon for the new
 *                          RowMenu row-actions component (Bookings list density
 *                          cleanup, CR D3).
 *   - 2026-08-06 Claude — Added `eye` icon for the Query List "View" row action.
 *   - 2026-09-16 Claude — Added `eyeOff` (the struck-through eye), the sibling
 *                          `eye` needed for PasswordInput's reveal toggle. Same
 *                          Feather geometry as `eye`, so the two swap in place
 *                          without the glyph jumping.
 */
const PATHS = {
  grid:'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  plane:'M21 16l-8-5V4a1 1 0 00-2 0v7l-8 5v2l8-3v4l-2 1v1l3-1 3 1v-1l-2-1v-4l8 3z',
  flight:'M21 16l-8-5V4a1 1 0 00-2 0v7l-8 5v2l8-3v4l-2 1v1l3-1 3 1v-1l-2-1v-4l8 3z',
  bed:'M3 7v11M3 13h18v5M21 13v-2a3 3 0 00-3-3H9v5',
  edit:'M12 20h8M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z',
  menu:'M4 6h16M4 12h16M4 18h16',
  box:'M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8',
  page:'M14 3v5h5M6 3h9l5 5v13H6zM9 13h6M9 17h6',
  badge:'M12 2l3 3h4v4l3 3-3 3v4h-4l-3 3-3-3H5v-4l-3-3 3-3V5h4z',
  bars:'M4 6h16M4 12h16M4 18h16',
  download:'M12 3v12M7 11l5 5 5-5M4 21h16',
  plus:'M12 5v14M5 12h14',
  trend:'M3 17l6-6 4 4 8-8M21 7v6h-6',
  check:'M20 6L9 17l-5-5',
  x:'M18 6L6 18M6 6l12 12',
  expand:'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
  refund:'M3 12a9 9 0 109-9 9 9 0 00-7 3.3M3 3v4h4',
  filter:'M3 5h18l-7 8v5l-4 2v-7z',
  trash:'M3 6h18M8 6V4h8v2M5 6l1 14h12l1-14',
  chev:'M9 6l6 6-6 6',
  logout:'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
  wallet:'M3 9h13a2 2 0 012 2M16 12h3',
  server:'M7 7.5h.01M7 16.5h.01',
  card:'M2 10h20',
  bell:'M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0',
  clock:'M12 7v5l3 2',
  search:'M21 21l-4-4',
  users:'M3 20a6 6 0 0112 0M16 6a3 3 0 010 6M21 20a6 6 0 00-4-5.6',
  gear:'M19 12a7 7 0 00-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 00-2-1.2l-.4-2.5H10l-.4 2.5a7 7 0 00-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 005 12a7 7 0 00.1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 002 1.2l.4 2.5h4l.4-2.5a7 7 0 002-1.2l2.3 1 2-3.4-2-1.5A7 7 0 0019 12z',
  eye:'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z',
  // One `d` holding every subpath: the two half-lids, the crossed pupil and
  // the slash. Icon renders a single <path>, and EXTRA is for shapes a path
  // cannot express (circles/rects) — none of these need it.
  eyeOff:'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22',
};

const EXTRA = {
  wallet: <rect x="3" y="6" width="18" height="14" rx="3" />,
  server: <><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /></>,
  card:   <rect x="2" y="5" width="20" height="14" rx="3" />,
  clock:  <circle cx="12" cy="12" r="9" />,
  search: <circle cx="11" cy="11" r="7" />,
  users:  <circle cx="9" cy="8" r="3" />,
  gear:   <circle cx="12" cy="12" r="3.2" />,
  badge:  <circle cx="12" cy="12" r="3" />,
  more: <>
    <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
  </>,
  mail: <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <polyline points="3,7 12,13 21,7" />
  </>,
  eye: <circle cx="12" cy="12" r="3" />,
};

export default function Icon({ name, size = 18, className = '', ...rest }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...rest}
    >
      {EXTRA[name]}
      {PATHS[name] ? <path d={PATHS[name]} /> : null}
    </svg>
  );
}
